/**
 * Cache configuration constants that match the backend Redis TTLs
 * These values should be used for keepUnusedDataFor and refetchOnMountOrArgChange
 */

import { config } from '@/config/config';
import { apiLogger } from '@/core/utils/logger';

export const CACHE_CONFIG = {
  // User-related caching (matches backend UserCache: 30 minutes)
  USER: {
    keepUnusedDataFor: 30 * 60,      // 30 minutes
    refetchOnMountOrArgChange: 25 * 60, // 25 minutes (refresh before backend cache expires)
  },
  
  // Organization-related caching (matches backend OrganizationCache: 1 hour)
  ORGANIZATION: {
    keepUnusedDataFor: 60 * 60,      // 1 hour
    refetchOnMountOrArgChange: 50 * 60, // 50 minutes
  },
  
  // Tags and folders (static data, matches backend TagsCache: 1 hour)
  STATIC_DATA: {
    keepUnusedDataFor: 60 * 60,      // 1 hour
    refetchOnMountOrArgChange: 50 * 60, // 50 minutes
  },
  
  // Conversation metadata (optimized for navigation reliability)
  CONVERSATION: {
    keepUnusedDataFor: 45 * 60,      // 45 minutes - longer cache to survive navigation
    refetchOnMountOrArgChange: 5 * 60,   // Refetch if older than 5 minutes (reduced from 30s to prevent request storms)
  },
  
  // Real-time data (messages, presence)
  REAL_TIME: {
    keepUnusedDataFor: 120,          // 2 minutes - keeps recently viewed conversations cached for smoother switching
    refetchOnMountOrArgChange: false, // Don't auto-refetch, use WebSocket
  },
  
  // Session/auth data (matches backend SessionCache: 5 minutes)
  SESSION: {
    keepUnusedDataFor: 5 * 60,       // 5 minutes
    refetchOnMountOrArgChange: 4 * 60,  // 4 minutes
  },
  
  // Default (current settings)
  DEFAULT: {
    keepUnusedDataFor: 30 * 60,      // 30 minutes - increased for better navigation
    refetchOnMountOrArgChange: 60,   // Refetch if older than 60 seconds
  },
  
  // Contacts-specific caching
  CONTACTS: {
    keepUnusedDataFor: 25 * 60,      // 25 minutes - contacts don't change often
    refetchOnMountOrArgChange: 20 * 60, // 20 minutes
  }
} as const;

/**
 * Helper function to log cache status in development
 */
export const logCacheStatus = (endpoint: string, status: 'HIT' | 'MISS', ttl?: number) => {
  if (config.features.debugLogging) {
    const color = status === 'HIT' ? 'color: green' : 'color: orange';
    apiLogger.debug(
      `%c[Cache ${status}] ${endpoint}`,
      color,
      ttl ? `TTL: ${ttl}s` : ''
    );
  }
};